import { z } from "zod";

export const CostBreakdownItemSchema = z.object({
  name: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
  pct_of_total: z.number(),
  record_count: z.number().nullable().optional(),
});

export const CostBreakdownResponseSchema = z.object({
  breakdown: z.array(CostBreakdownItemSchema),
  group_by: z.string(),
  total_dbus: z.number(),
  total_cost: z.number(),
});

export type CostBreakdownItem = z.infer<typeof CostBreakdownItemSchema>;
export type CostBreakdownResponse = z.infer<typeof CostBreakdownResponseSchema>;

export interface SkuCostParams {
  start_date: string;
  end_date: string;
  workspace_id?: string;
}

export interface SkuBreakdownParams extends SkuCostParams {
  group_by?: "sku" | "workspace" | "product";
  limit?: number;
  sortAttribute?: string;
  sortOrder?: "asc" | "desc";
  /** Multi-value name filter — sent as repeated `sku_names=...` (for SKU tab) or `products=...` (for Product tab). */
  sku_names?: string[];
  products?: string[];
}

// Compute efficiency schemas
export const ComputeTypeBreakdownSchema = z.object({
  compute_type: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
  pct_of_total: z.number(),
});

export const DeploymentTypeBreakdownSchema = z.object({
  deployment_type: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
  pct_of_total: z.number(),
});

export const EngineTypeBreakdownSchema = z.object({
  engine_type: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
  pct_of_total: z.number(),
});

export const ComputeEfficiencyResponseSchema = z.object({
  jobs_pct: z.number(),
  all_purpose_pct: z.number(),
  efficiency_score: z.string(),
  compute_types: z.array(ComputeTypeBreakdownSchema),
  deployment_types: z.array(DeploymentTypeBreakdownSchema),
  engine_types: z.array(EngineTypeBreakdownSchema),
  total_dbus: z.number(),
  total_cost: z.number(),
});

export type ComputeTypeBreakdown = z.infer<typeof ComputeTypeBreakdownSchema>;
export type DeploymentTypeBreakdown = z.infer<typeof DeploymentTypeBreakdownSchema>;
export type EngineTypeBreakdown = z.infer<typeof EngineTypeBreakdownSchema>;
export type ComputeEfficiencyResponse = z.infer<typeof ComputeEfficiencyResponseSchema>;

// Trends — by deployment type (serverless vs provisioned)
export const DeploymentTypeTrendItemSchema = z.object({
  period: z.string(),
  deployment_type: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
});

export const DeploymentTypeTrendsResponseSchema = z.object({
  trends: z.array(DeploymentTypeTrendItemSchema),
  deployment_types: z.array(z.string()),
  granularity: z.string(),
});

export type DeploymentTypeTrendItem = z.infer<typeof DeploymentTypeTrendItemSchema>;
export type DeploymentTypeTrendsResponse = z.infer<typeof DeploymentTypeTrendsResponseSchema>;

// Trends — by engine type (photon vs standard)
export const EngineTypeTrendItemSchema = z.object({
  period: z.string(),
  engine_type: z.string(),
  total_dbus: z.number(),
  estimated_cost: z.number(),
});

export const EngineTypeTrendsResponseSchema = z.object({
  trends: z.array(EngineTypeTrendItemSchema),
  engine_types: z.array(z.string()),
  granularity: z.string(),
});

export type EngineTypeTrendItem = z.infer<typeof EngineTypeTrendItemSchema>;
export type EngineTypeTrendsResponse = z.infer<typeof EngineTypeTrendsResponseSchema>;

export interface TypeTrendsParams {
  start_date: string;
  end_date: string;
  granularity?: "day" | "week" | "month";
  workspace_id?: string;
}
